import { useDashboardAnalytics } from 'store/api/analytics/use-dashboard-analytics';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { PieTooltipProps } from '@nivo/pie';
import { useTheme } from 'styled-components';

import { isSmallDesktop } from 'hooks';
import { useAppSelector } from 'store';
import { mapQueries } from 'utils/query';
import { capitalize } from 'utils/string';
import { formatPrice } from 'utils/helpers';
import { defaultColors } from 'utils/charts';
import { useDetailsQuery } from 'store/api/event';
import { selectAuthUser } from 'store/slices/auth';
import { formatDate, validateDateRange } from 'utils/date';
import { useDashboardStatisticsQuery } from 'store/api/event';
import { AnalyticsEmployee } from 'store/api/analytics';
import {
  Row,
  Col,
  Flex,
  Details,
  InfoCard,
  PieChart,
  InfoBlock,
  LineChart,
  SalesList,
  DatePicker,
  Placeholder,
  RefundsList,
  ErrorMessage,
  ExchangesList,
  ProductSoldList,
  PieChartDataProps,
  PaymentMethodsList,
  ChartClientTooltip,
  TodayEmployeesList,
  EmployeeDrawerAnalytics,
} from 'components';

import * as Styles from './styles';
import { EventDashboardProps } from './types';
import {
  getStockInfo,
  prepareChartData,
  getSalesOverviewInfo,
  getWastageProductsInfo,
} from './config';

const PieTooltip = (tooltipProps: PieTooltipProps<PieChartDataProps>) => (
  <ChartClientTooltip
    {...tooltipProps}
    {...tooltipProps.datum}
    isRenderRawValue
    subtitle={`Quantity: ${tooltipProps.datum.value}`}
    value={formatPrice(tooltipProps.datum.data.price)}
  />
);

export const EventDashboard = ({
  id,
  hideCalendar,
  initDateRange,
  withoutPadding,
  hideOverlay = false,
  hideEmployeeCommissions,
  withDateRangeRestriction,
  datePickerContainerPadding,
}: EventDashboardProps) => {
  const theme = useTheme();
  const smallDesktop = isSmallDesktop();
  const authUser = useAppSelector(selectAuthUser);
  const [employee, setEmployee] = useState<AnalyticsEmployee>();
  const [dateRangeError, setDateRangeError] = useState<string>('');
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(
    initDateRange?.length ? initDateRange : [new Date(), new Date()]
  );

  const endDate = formatDate(dateRange[1]);
  const startDate = formatDate(dateRange[0]);
  const commonSkip = {
    skip: !id || !dateRange[0] || !dateRange[1] || !!dateRangeError,
  };

  const {
    data: analytics,
    loadMore,
    ...analyticsProps
  } = useDashboardAnalytics(
    {
      endDate,
      startDate,
      eventIds: JSON.stringify([Number(id)]),
      companyIds: JSON.stringify([authUser?.company?.id]),
    },
    commonSkip
  );

  const { data, ...props } = useDashboardStatisticsQuery(
    { id: id!, endDate, startDate },
    commonSkip
  );

  const { data: event, ...eventProps } = useDetailsQuery(id!, { skip: !id });

  const commonProps = { data, theme };
  const drawerProps = useMemo(
    () => ({
      hideOverlay,
      topOffset: '15%',
    }),
    [hideOverlay]
  );

  const stockInfo = getStockInfo(commonProps);
  const lineChartData = prepareChartData(data?.dailySales);
  const salesOverviewInfo = getSalesOverviewInfo(commonProps);
  const wastageProductsInfo = getWastageProductsInfo(commonProps);

  const minDate = withDateRangeRestriction
    ? dayjs(event?.startDate).toDate()
    : undefined;
  const maxDate =
    withDateRangeRestriction && dayjs(event?.endDate).isBefore(new Date())
      ? dayjs(event?.endDate).toDate()
      : new Date();

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    setDateRange(dates);
    validateDateRange(dates, setDateRangeError);
  };

  return (
    <>
      <Details {...mapQueries([props, eventProps, analyticsProps])}>
        {!hideCalendar && (
          <Styles.DatePickerContainer
            datePickerContainerPadding={datePickerContainerPadding}
          >
            <DatePicker
              selectsRange
              changeByButton
              monthsShown={2}
              minDate={minDate}
              maxDate={maxDate}
              endDate={dateRange[1]}
              startDate={dateRange[0]}
              onChange={handleDateChange}
            />
            {dateRangeError && <ErrorMessage>{dateRangeError}</ErrorMessage>}
          </Styles.DatePickerContainer>
        )}
        {!(
          data?.checkoutCounts ||
          analytics?.refunds?.length ||
          analytics?.exchanges?.length
        ) && !props.isLoading ? (
          <Placeholder text="There have been no sales on this event yet" />
        ) : (
          <Styles.Container withoutPadding={withoutPadding}>
            <Flex gap={16} flex={2} flexWrap="wrap" flexDirection="column">
              <InfoBlock title="Sales Overview">
                <Row>
                  {salesOverviewInfo.map((info, index) => (
                    <Col key={index} colTablet={4} col={smallDesktop ? 4 : 3}>
                      <InfoCard {...info} />
                    </Col>
                  ))}
                </Row>
              </InfoBlock>

              <InfoBlock title="Sales Statistics">
                {!!data?.dailySales?.length ? (
                  <LineChart
                    height="352px"
                    data={lineChartData}
                    axisLeft={{ format: '$', tickValues: 5 }}
                  />
                ) : (
                  <Placeholder text="You have no data yet" />
                )}
              </InfoBlock>
            </Flex>

            <Styles.RightContainer>
              <Styles.RightWrap>
                <InfoBlock title="Stock">
                  <Row>
                    {stockInfo.map((stock, indx) => (
                      <Col key={indx}>
                        <InfoCard {...stock} />
                      </Col>
                    ))}
                  </Row>
                </InfoBlock>
                <InfoBlock title="Wastage Product">
                  {wastageProductsInfo.map((info, index) => (
                    <Row key={index}>
                      <Col col={12} colTablet={12}>
                        <InfoCard {...info} />
                      </Col>
                    </Row>
                  ))}
                </InfoBlock>
              </Styles.RightWrap>

              <InfoBlock title="Top Selling Items">
                <Styles.PieContainer>
                  <PieChart
                    title="Products"
                    tooltip={PieTooltip}
                    data={
                      data?.topSellingProducts?.map((item) => ({
                        price: item.sales,
                        value: item.quantity,
                        id: item.product?.name,
                        label: item.product?.name,
                      })) || []
                    }
                  />

                  <Flex gap={8} fullWidth justifyContent="center">
                    {data?.topSellingProducts?.map((item, i) => (
                      <Flex key={i} alignItems="center">
                        <Styles.Dot
                          bg={defaultColors[i % defaultColors.length]}
                        />
                        <p>{capitalize(String(item.product?.name))}</p>
                      </Flex>
                    ))}
                  </Flex>
                </Styles.PieContainer>
              </InfoBlock>
            </Styles.RightContainer>
          </Styles.Container>
        )}

        <Styles.ListsContainer>
          <PaymentMethodsList data={analytics?.paymentMethods} />

          <TodayEmployeesList
            title="Employee Sales"
            setEmployee={setEmployee}
            data={analytics?.employeeSales}
            timezoneCode={event?.timezone?.code}
          />

          <SalesList
            loadMoreProps={loadMore.sales}
            drawerProps={drawerProps}
            data={analytics?.checkouts}
          />

          <RefundsList
            loadMoreProps={loadMore.refunds}
            {...drawerProps}
            data={analytics?.refunds}
          />

          <ProductSoldList data={analytics?.productSales} />

          <ExchangesList
            loadMoreProps={loadMore.exchanges}
            {...drawerProps}
            data={analytics?.exchanges}
          />
        </Styles.ListsContainer>
      </Details>

      <EmployeeDrawerAnalytics
        overModal
        topOffset="15%"
        opened={!!employee}
        employee={employee}
        endDate={dateRange[1]}
        startDate={dateRange[0]}
        hideOverlay={hideOverlay}
        onClose={() => setEmployee(undefined)}
        hideEmployeeCommissions={hideEmployeeCommissions}
        saleDrawerProps={{
          ...drawerProps,
          overModal: true,
        }}
      />
    </>
  );
};
